import React, { useCallback, useMemo } from "react";
import styles from "./Item.module.scss";
import Image from "@components/Image";
import cx from "classnames";
import Button from "@components/Button";
import Link from "@components/Link";

import useWindowSize from "@hooks/useWindowSize";
import _ from "lodash";

type Placement =
  | "left"
  | "right"
  | "topLeft"
  | "topRight"
  | "bottomLeft"
  | "bottomRight";

interface IImage {
  placement: Placement;
  src: string;
  alt: string;
  layoutClassName?: string | { [key: string]: string };
  className?: string | { [key: string]: string };
}

interface ImagesElement {
  placement: Placement;
  node: React.ReactNode;
  layoutClassName?: string | { [key: string]: string };
}

interface IProps {
  className: string | { [key: string]: string };
  images: IImage[] | ImagesElement;
  aside: {
    placement: Placement;
    className: string | { [key: string]: string };
    content: {
      className: string | { [key: string]: string };
      title: string;
      text: string | string[] | React.ReactNode;
    };
  };
  showGetToKnow?: boolean;
  button?: {
    label?: string;
    href?: string;
  };
}

export default function AboutItem({
  className,
  images,
  aside,
  showGetToKnow = false,
  button,
}: IProps) {
  //TODO: refactor

  const imageLayout = useCallback((imagePlacement: IImage["placement"]) => {
    switch (imagePlacement) {
      case "left":
        return styles.imageLayoutLeft;
      case "right":
        return styles.imageLayoutRight;
      case "topLeft":
        return styles.imageLayoutTopLeft;
      case "topRight":
        return styles.imageLayoutTopRight;
      case "bottomLeft":
        return styles.imageLayoutBottomLeft;
      case "bottomRight":
        return styles.imageLayoutBottomRight;
    }
  }, []);

  const asideLayout = useMemo(() => {
    switch (aside.placement) {
      case "left":
        return styles.asideLayoutLeft;
      case "right":
        return styles.asideLayoutRight;
      case "topLeft":
        return styles.asideLayoutTopLeft;
      case "topRight":
        return styles.asideLayoutTopRight;
      case "bottomLeft":
        return styles.asideLayoutBottomLeft;
      case "bottomRight":
        return styles.asideLayoutBottomRight;
    }
  }, [aside.placement]);

  const description = useMemo(() => {
    if (Array.isArray(aside.content.text)) {
      return (
        <div className="flex flex-col">
          {aside.content.text.map((t, index) => (
            <div className="flex" key={index}>
              <div className="px-2">{`•`}</div>
              <div>{t}</div>
            </div>
          ))}
        </div>
      );
    }
    return aside.content.text;
  }, [aside.content.text]);
  const { width = 1280 } = useWindowSize();
  if (width <= 576) {
    return (
      <div
        className={cx(
          "flex flex-col items-center w-full",
          styles.itemSectionMobile
        )}
      >
        {Array.isArray(images)
          ? images.map((image, index) => (
              <div key={index}>
                <Image
                  key={index}
                  wrapperProps={{ className: cx(styles.image) }}
                  src={image.src}
                  alt={image.alt}
                  layout="fill"
                />
              </div>
            ))
          : images.node}
        <div className={cx("flex flex-col mt-8")}>
          <div className={cx(styles.title)}>{aside.content.title}</div>
          <div className={cx(styles.description)}>{description}</div>
          {showGetToKnow && (
            <Link href={_.get(button, "href", "#")}>
              <Button
                type="icon"
                suffix="shape-arrow"
                className={cx(styles.readMore, "mt-8")}
                label={_.get(button, "label")}
              />
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={cx(
          "relative flex w-full",
          className,
          styles.itemSectionDesktop
        )}
      >
        {Array.isArray(images) ? (
          images.map((image, index) => (
            <div
              key={index}
              className={cx(
                "absolute z-10",
                image?.layoutClassName ?? imageLayout(image.placement)
              )}
            >
              <Image
                wrapperProps={{ className: cx(styles.image, image?.className) }}
                src={image.src}
                alt={image.alt}
                layout="fill"
              />
            </div>
          ))
        ) : (
          <div
            className={cx(
              "absolute z-10",
              images?.layoutClassName ?? imageLayout(images.placement)
            )}
          >
            {images.node}
          </div>
        )}

        <div
          className={cx(
            "absolute flex items-center",
            styles.asideLayoutBackground,
            asideLayout,
            aside.className
          )}
        >
          <div className={cx("flex flex-col", aside.content.className)}>
            <div className={cx(styles.title)}>{aside.content.title}</div>
            <div className={cx(styles.description)}>{description}</div>
            {showGetToKnow && (
              <Link href={_.get(button, "href", "#")}>
                <Button
                  type="icon"
                  suffix="shape-arrow"
                  className={cx(styles.readMore, "mt-8")}
                  label={
                    _.get(button, "label")
                    // i18n.t("Button.getToKnowOurExecutiveChef")
                  }
                />
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
